/**
 * R3.2 UX — création rapide taxonomie (picker commerçant).
 * Codes auto + defaults cohérents par famille. Pas de champs techniques.
 */

/** Accent / apostrophe → ASCII approx pour slugs. */
function foldAscii(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '')
    .replace(/&/g, ' et ');
}

/**
 * Code famille : a-z, chiffres, underscore (ex. traiteur_local).
 */
export function slugifyFamilyId(label) {
  let s = foldAscii(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  if (!s) s = 'famille';
  if (!/^[a-z]/.test(s)) s = `f_${s}`;
  return s.slice(0, 48);
}

/**
 * Code métier : A-Z, chiffres, underscore (ex. TRAITEUR_LOCAL).
 */
export function slugifyMetierId(label) {
  let s = foldAscii(label)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  if (!s) s = 'METIER';
  if (!/^[A-Z]/.test(s)) s = `M_${s}`;
  return s.slice(0, 48);
}

/**
 * Assure unicité parmi une liste d'ids existants (suffixe _2, _3…).
 */
export function ensureUniqueId(base, existingIds = []) {
  const taken = new Set((existingIds || []).map((x) => String(x)));
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${base}_${i}`.slice(0, 48);
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}_${Date.now().toString(36)}`.slice(0, 48);
}

/**
 * Defaults métier à la création rapide, dérivés de la famille.
 * catalogOrientation / profileKey = identité soft (C10), jamais des gates UX.
 */
export function getFamilyQuickDefaults(familyId) {
  const key = String(familyId || '').trim().toLowerCase();
  const byFamily = {
    restauration: {
      catalogOrientation: 'PRODUITS',
      profileKey: 'RESTAURANT',
      moduleProduits: true,
      modulePrestations: false,
      livraison: true,
      clickAndCollect: true,
      reservationTable: false,
      priseRDV: false,
      priseCreneaux: false,
      moduleCreneaux: false,
    },
    retail: {
      catalogOrientation: 'PRODUITS',
      profileKey: 'BOUTIQUE',
      moduleProduits: true,
      modulePrestations: false,
      livraison: true,
      clickAndCollect: true,
      reservationTable: false,
      priseRDV: false,
      priseCreneaux: false,
      moduleCreneaux: false,
    },
    beaute: {
      catalogOrientation: 'PRESTATIONS',
      profileKey: 'SPA',
      moduleProduits: false,
      modulePrestations: true,
      livraison: false,
      clickAndCollect: false,
      reservationTable: false,
      priseRDV: true,
      priseCreneaux: true,
      moduleCreneaux: false,
    },
    reservation_espaces: {
      catalogOrientation: 'PRESTATIONS',
      profileKey: 'MULTISPORT',
      moduleProduits: false,
      modulePrestations: true,
      livraison: false,
      clickAndCollect: false,
      reservationTable: false,
      priseRDV: true,
      priseCreneaux: true,
      moduleCreneaux: true,
    },
    loisirs: {
      catalogOrientation: 'PRESTATIONS',
      profileKey: 'LOISIRS',
      moduleProduits: false,
      modulePrestations: true,
      livraison: false,
      clickAndCollect: false,
      reservationTable: false,
      priseRDV: true,
      priseCreneaux: true,
      moduleCreneaux: false,
    },
    services_pro: {
      catalogOrientation: 'PRESTATIONS',
      profileKey: 'SERVICE',
      moduleProduits: false,
      modulePrestations: true,
      livraison: false,
      clickAndCollect: false,
      reservationTable: false,
      priseRDV: true,
      priseCreneaux: true,
      moduleCreneaux: false,
    },
    autre: {
      catalogOrientation: 'MIXTE',
      profileKey: 'BOUTIQUE',
      moduleProduits: true,
      modulePrestations: true,
      livraison: false,
      clickAndCollect: true,
      reservationTable: false,
      priseRDV: false,
      priseCreneaux: false,
      moduleCreneaux: false,
    },
  };
  return (
    byFamily[key] || {
      catalogOrientation: 'MIXTE',
      profileKey: 'CUSTOM',
      moduleProduits: true,
      modulePrestations: false,
      livraison: false,
      clickAndCollect: true,
      reservationTable: false,
      priseRDV: false,
      priseCreneaux: false,
      moduleCreneaux: false,
    }
  );
}

/** Payload POST famille — création rapide picker. */
export function buildQuickFamilyPayload(label, { existingFamilyIds = [] } = {}) {
  const trimmed = String(label || '').trim();
  if (!trimmed) throw new Error('Libellé famille requis');
  const id = ensureUniqueId(slugifyFamilyId(trimmed), existingFamilyIds);
  return { id, label: trimmed };
}

/**
 * Payload POST métier — création rapide picker.
 * @param {string} label
 * @param {string} familyId
 * @param {{ existingMetierIds?: string[], familyIcon?: string|null }} [opts]
 */
export function buildQuickMetierPayload(label, familyId, opts = {}) {
  const trimmed = String(label || '').trim();
  if (!trimmed) throw new Error('Libellé métier requis');
  if (!familyId) throw new Error('Famille requise');
  const id = ensureUniqueId(slugifyMetierId(trimmed), opts.existingMetierIds || []);
  const defaults = getFamilyQuickDefaults(familyId);
  return {
    id,
    familyId,
    label: trimmed,
    icon: opts.familyIcon || null,
    profileKey: defaults.profileKey || id,
    catalogOrientation: defaults.catalogOrientation,
    moduleProduits: defaults.moduleProduits,
    modulePrestations: defaults.modulePrestations,
    moduleCaisse: false,
    moduleCreneaux: defaults.moduleCreneaux,
    priseRDV: defaults.priseRDV,
    priseCreneaux: defaults.priseCreneaux,
    reservationTable: defaults.reservationTable,
    livraison: defaults.livraison,
    clickAndCollect: defaults.clickAndCollect,
    paiementEnLigne: true,
    paiementSurPlace: true,
    devise: 'EUR',
  };
}
